import type { TrackInfo } from '@shared/types.ts';

/**
 * Formatting for the CURRENT SLOT — the time a track or programme occupies.
 *
 * This lives on the client, not the server, on purpose: the wire carries
 * `startedAtUtc` and `durationMs`, and turning those into "8:00 – 9:00 PM"
 * needs the viewer's timezone and locale. A server that formatted it would
 * bake n8-server's timezone into a phone in another one.
 */

function clock(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "8:00 – 9:00 PM", or null when the feed gave us no usable times. */
export function slotLabel(track: TrackInfo | null): string | null {
  if (!track?.startedAtUtc || !track.durationMs) return null;
  const start = new Date(track.startedAtUtc);
  const end = new Date(start.getTime() + track.durationMs);
  if (Number.isNaN(start.getTime())) return null;
  return `${clock(start)} – ${clock(end)}`;
}

/**
 * How far into the slot we are, plus how long is left.
 *
 * The remaining time is what drives a CSS animation rather than a timer: a
 * progress bar that ticks in JavaScript costs a render a second, forever, on
 * a device that has none to spare. Handing the compositor a duration lets it
 * run the whole bar without waking React again.
 */
export function slotProgress(
  track: TrackInfo | null,
): { fraction: number; remainingMs: number } | null {
  if (!track?.startedAtUtc || !track.durationMs) return null;
  const start = Date.parse(track.startedAtUtc);
  if (!Number.isFinite(start)) return null;

  const elapsed = Date.now() - start;
  if (elapsed < 0 || elapsed >= track.durationMs) return null;

  return {
    fraction: elapsed / track.durationMs,
    remainingMs: track.durationMs - elapsed,
  };
}

/** The second line under a title: a time slot for TV, the artist for radio. */
export function secondaryLine(
  track: TrackInfo | null, kind: 'tv' | 'radio',
): string | null {
  if (!track) return null;
  return kind === 'tv' ? slotLabel(track) : track.artist;
}
